import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import {
  useInfiniteHits as useAlgoliaInfiniteHits,
  useInstantSearch as useAlgoliaInstantSearch,
} from 'react-instantsearch';
import type { CmsSlotsDeliveryKeys } from '@storefront/lib-global';
import {
  CMS_COMPONENTS_DISPLAY_PROPERTIES,
  CMS_SLOTS_BY_TYPE,
  CmsSlot,
  getAlgoliaProductIndex,
  getItem,
  useGetProductListingCmsComponents,
} from '@storefront/lib-global';
import type { Category } from '@storefront/lib-global/codegen/types';
import { useHistory } from '@storefront/lib-global/context/historyContext';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { useStaticContext } from '@storefront/lib-global/context/staticContext';
import type { AlgoliaInfiniteHits } from '@storefront/lib-global/globalComponents/algolia/AlgoliaHits/AlgoliaHits';
import type { HandlebarTemplateProps } from '@storefront/lib-global/globalComponents/handlebarTemplate';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { ArrowDown } from '@storefront/lib-global/icons';
import { sendClickAfterSearchEvent } from '@storefront/lib-global/services/algolia/insights/events';
import { media } from '@storefront/lib-global/stylings';
import { createURLParameter } from '@storefront/lib-global/utils/createURLParameter';
import { sendLoadMoreItemsEvent } from '@storefront/lib-global/utils/gtm/events/load_more_items/sendLoadMoreItemsEvent';
import { sendViewItemListFromListingEvent } from '@storefront/lib-global/utils/gtm/events/view_item_list/sendViewItemListFromListingEvent';
import { Locale } from '@storefront/lib-global/utils/internationalization/localeHelper';
import ProductListItem from '../ProductListItem/ProductListItem';
import { SlotAndDescription } from '../productList/SlotAndDescription';
import {
  CategoryName,
  ContentBannerWrapper,
  PageDetail,
  ProductListContainer,
  ProductListWrapper,
  StyledWhiteButton,
} from './ProductListingHits.styles';

type ProductListingHitsProps = AlgoliaInfiniteHits & {
  category?: Category;
  handlebarComponents?: HandlebarTemplateProps[] | null;
  isMobile?: boolean;
};
enum ProductListingType {
  PLP = 'PLP',
  SRP = 'SRP',
}

export function ProductListingHits({ category, handlebarComponents, isMobile, ...props }: ProductListingHitsProps) {
  const { host, baseUrl } = useStaticContext();
  const { locale } = useLocaleContext();
  const router = useRouter();
  const { lastPath } = useHistory();
  const { status } = useAlgoliaInstantSearch();
  const isDesktop = useMediaMatch(media.greaterThan('lg'), !isMobile);

  const { query, asPath = '' } = router;
  const { page, query: queryURL } = query;
  const pageNumber = parseInt(page as string, 10) || 1;
  const { hits, showMore, results, currentPageHits } = useAlgoliaInfiniteHits(props);
  const eventName = category ? 'PLP: Product Clicked' : 'SRP: Product Clicked';
  const componentType = category ? ProductListingType.PLP : ProductListingType.SRP;
  const isPLP = asPath.endsWith('-c');

  const contentCards = useGetProductListingCmsComponents({
    field: CMS_COMPONENTS_DISPLAY_PROPERTIES[componentType].CARD_POSITION,
    slots: CMS_SLOTS_BY_TYPE[componentType].CARDS,
    type: componentType,
  });
  const contentBanners = useGetProductListingCmsComponents({
    field: CMS_COMPONENTS_DISPLAY_PROPERTIES[componentType].BANNER_POSITION,
    slots: CMS_SLOTS_BY_TYPE[componentType].BANNERS,
    type: componentType,
  });

  const cardsPerRow = isDesktop ? 3 : 2;

  const cardsList = useMemo(() => {
    const list: (typeof hits[number] | CmsSlotsDeliveryKeys)[] = [...hits];

    contentCards.forEach(([cmsSlotName, cardPosition]) => {
      // Check if contentCard position fits current lenght of product hits list.
      if (cardPosition - 1 >= list.length) return;

      list.splice(cardPosition - 1, 0, cmsSlotName);
    });

    return list;
  }, [hits, contentCards]);

  const cardsRows = useMemo(() => Math.ceil(cardsList.length / cardsPerRow), [cardsList, cardsPerRow]);

  const [loadMore, setLoadMore] = useState(false);
  const prevPageHits = useRef<typeof hits>(hits);
  const { t } = useTranslation(['lib-products-common']);

  const searchParams = createURLParameter({
    query: queryURL,
    page: (pageNumber + 1).toString(),
  });

  const itemList = category
    ? { id: category?.key, name: category?.name }
    : { id: 'search_results_page', name: 'Search results page' };
  const isOutlet: boolean = [Locale.CT_OUTLET_LOCALE, Locale.STOREFRONT_OUTLET_LOCALE].includes(locale);
  const localePath = isOutlet ? '' : `/${locale.toLocaleLowerCase()}`;
  const pathWithoutPage = category ? asPath?.replace(/(\?|&)page=\d+/g, '') : '';

  useEffect(() => {
    if (query.page && Number(query.page) > 1 && results) {
      const hitsIndex = (Number(query.page) - 1) * (Number(query.pageSize) || results.hitsPerPage);
      const productItem = document.getElementById(hits[hitsIndex]?.objectID);
      productItem?.scrollIntoView(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // used to save previous path to check if path (filters) has changed
  const prevAsPath = useRef('');
  // used to save previous hits in form of stringified JSON to check if
  const prevHits = useRef('');
  // used to prevent additional event calls on extra rerenders based on timeout
  const eventDebounceFlag = useRef(true);

  const eventDebounceTime = 200;
  const eventTimeout = 1_500;

  useEffect(() => {
    const runEvent = (items: typeof hits) => {
      const searchItemList = { itemListId: 'search_results_page', itemListName: 'Search results page' };
      const categoryItemList = { itemListId: category?.key, itemListName: category?.name as string };

      sendViewItemListFromListingEvent({
        products: items,
        itemList: category ? categoryItemList : searchItemList,
        locale,
        baseUrl,
      });

      prevAsPath.current = asPath;
      prevHits.current = JSON.stringify(hits);
      // block extra event runs by setting flag to false
      eventDebounceFlag.current = false;

      // set timeout for unblocking event runs by setting flag back to true. Change timeout if needed
      setTimeout(() => {
        eventDebounceFlag.current = true;
      }, eventDebounceTime);
    };

    let hitsTimeout: ReturnType<typeof setTimeout>;

    // run event checks only if event debounce flag is true
    if (eventDebounceFlag.current) {
      // check if path (filters) has changed
      if (asPath !== prevAsPath.current) {
        // in case of slow internet connection OR if hits are the same for different path (filters)
        // which will not run next `if`, we are sending event with current hits after timeout. Adjust if needed
        hitsTimeout = setTimeout(() => {
          runEvent(hits);
        }, eventTimeout);

        // path changes before hits, so after path change check if hits changed
        if (JSON.stringify(hits) !== prevHits.current) {
          // cancel sending event after timeout
          clearTimeout(hitsTimeout);
          runEvent(hits);
        }
      }
    }
    return () => {
      clearTimeout(hitsTimeout);
    };
  }, [asPath, category, hits, baseUrl, locale]);

  useEffect(() => {
    // used to scroll to product using back button from browser
    if (lastPath?.endsWith('-p')) {
      const productKey = lastPath.split('/').slice(-1)[0].replace('-p', '');
      const productItem = document.getElementById(productKey);
      productItem?.scrollIntoView(true);
    }
  }, [lastPath]);

  const loadMoreProducts = () => {
    prevPageHits.current = hits;
    showMore();
    setLoadMore(true);
  };

  const sendAlgoliaInsightsClickEvent = (objectId: string, cardPosition: number) => {
    const algoliaIndex = getAlgoliaProductIndex(locale);

    sendClickAfterSearchEvent({
      eventName,
      objectID: objectId,
      index: algoliaIndex,
      queryID: getItem('queryID') as string,
      position: cardPosition,
    });
  };

  useEffect(() => {
    if (loadMore && status === 'idle') {
      const searchItemList = { itemListId: 'search_results_page', itemListName: 'Search results page' };
      const categoryItemList = { itemListId: category?.key, itemListName: category?.name as string };
      const newPageProducts = hits.filter((hit) => !prevPageHits.current.includes(hit));

      if (newPageProducts.length > 0) {
        const firstNewProductId = newPageProducts[0].objectID;
        const productContainer = document.getElementById(firstNewProductId);
        const focusableElement = productContainer?.querySelector('a');
        focusableElement?.focus();
      }

      sendLoadMoreItemsEvent({
        products: hits,
        itemList: category ? categoryItemList : searchItemList,
        newProducts: newPageProducts,
        locale,
        baseUrl,
      });
      setLoadMore(false);
    }
  }, [category, currentPageHits, hits, loadMore, baseUrl, locale, status]);

  const slotAndDescriptionProps = {
    handlebarComponents,
    categoryDescription: category?.description,
    isDesktop,
    isPLP,
  };

  const getProductListCards = () => {
    if (!cardsList) return [];

    return cardsList?.map((product, i) => {
      if (typeof product === 'string') {
        return <CmsSlot deliveryKey={product as CmsSlotsDeliveryKeys} key={`${product}-${i}`} />;
      }

      return (
        <ProductListItem
          componentType={componentType}
          onClick={() => sendAlgoliaInsightsClickEvent(product.objectID, i + 1)}
          key={product.objectID}
          item={product}
          itemList={itemList}
          preload={i < 2}
          isMobile={isMobile}
        />
      );
    });
  };

  const getProductListCardsWithBanners = () => {
    const components = getProductListCards();

    contentBanners.forEach(([deliveryKey, bannerPosition], index) => {
      if (bannerPosition >= cardsRows) {
        return;
      }

      const gridRowStartPosition = bannerPosition + index + 1;
      const banner = (
        <ContentBannerWrapper position={gridRowStartPosition} key={`contentBannerWrapper-${gridRowStartPosition}`}>
          <CmsSlot deliveryKey={deliveryKey} key={gridRowStartPosition} />
        </ContentBannerWrapper>
      );

      components.splice(cardsPerRow * bannerPosition + index, 0, banner);
    });

    return components;
  };

  const productListComponents = getProductListCardsWithBanners();

  return (
    <ProductListWrapper>
      {isDesktop && (category?.h1Text || category?.name) && (
        <CategoryName data-testid="categoryName" aria-label={category?.h1Text || category?.name || ''}>
          {category?.h1Text || category?.name}
        </CategoryName>
      )}
      <ProductListContainer data-testid="plpContainer">{productListComponents}</ProductListContainer>
      <PageDetail data-testid="numberProductsPerPage">
        {t('product.list.paginate', { hits: hits.length, totalHits: results?.nbHits })}
      </PageDetail>
      {!!results?.nbHits && hits.length < results?.nbHits && (
        <>
          <StyledWhiteButton
            onClick={loadMoreProducts}
            type="button"
            disabled={status !== 'idle'}
            data-testid="loadMoreButton"
          >
            <ArrowDown />
            <span>{t('product.list.button')}</span>
          </StyledWhiteButton>
          <noscript>
            <a href={`https://${host}${localePath}${category ? pathWithoutPage : '/search'}${searchParams}`}>
              {t('product.list.button')}
            </a>
          </noscript>
        </>
      )}

      {isDesktop && <SlotAndDescription {...slotAndDescriptionProps} />}
    </ProductListWrapper>
  );
}

export default ProductListingHits;
